import { isLogicGroup } from 'customer-data-filters-ui/filterQueryFormat/logic/LogicGroup';
import { useCallback, useContext, useMemo } from 'react';
import { useViewerActions } from '../../hooks/useViewerActions';
import { List } from 'immutable';
import RefHighlightContainer from '../common/RefHighlightContainer';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import ViewerConditionClickableBox from '../viewer/ViewerConditionClickableBox';
import ViewerCondition from './ViewerCondition';
import ViewerRelatedConditions from './ViewerRelatedConditions';
import ViewerConditionsGroupQuickAddFilter from './ViewerConditionsGroupQuickAddFilter';
import invariant from 'react-utils/invariant';
import styled from 'foundations-theming/utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getConditionId from '../../utils/getConditionId';
import ViewerConditionPlaceholder from './ViewerConditionPlaceholder';
import { useStrategy } from '../../hooks/useStrategy';
import ViewerTestFiltersPropertyValueDisplay from './ViewerTestFiltersPropertyValueDisplay';
import ViewerTestFiltersGroupMatchDisplay from './ViewerTestFiltersGroupMatchDisplay';
import DeprecatedFilter from 'customer-data-filters-ui/filterQueryFormat/operator/DeprecatedFilter';
import MissingAssociationBranchFilter from '../../filterQueryFormat/operator/MissingAssociationBranchFilter';
import FilterGroupLogicGroupToggle from './FilterGroupLogicGroupToggle';
import { hasFlexibleLogicGroupConditions } from '../../permissions/gates';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ConditionsContainer = styled.div.withConfig({
  displayName: "ViewerConditionsGroupConditions__ConditionsContainer",
  componentId: "rwkzwh-0"
})(({
  theme
}) => ({
  '> * + *': {
    marginTop: theme.space[200]
  }
}));
const EmptyConditionsContainer = styled.div.withConfig({
  displayName: "ViewerConditionsGroupConditions__EmptyConditionsContainer",
  componentId: "rwkzwh-1"
})(({
  theme,
  useOnePanel
}) => ({
  padding: theme.space[200],
  border: `${useOnePanel ? '1px' : '2px'} solid ${theme.color['fill-info-default']}`,
  borderRadius: theme.borderRadius[100],
  backgroundColor: useOnePanel ? theme.color['fill-surface-default'] : theme.color['fill-accent-blue-subtle'],
  textAlign: 'center'
}));
const isNonEditableCondition = condition => {
  const dynamicAssociationPropertyRegExp = new RegExp(/^associations.\d-\d+$/, 'i');
  if (condition && condition.field && condition.field.name && dynamicAssociationPropertyRegExp.test(condition.field.name)) {
    return true;
  }
  if (condition && condition.constructor === DeprecatedFilter) {
    return true;
  }
  if (condition && condition.constructor === MissingAssociationBranchFilter) {
    return true;
  }
  return false;
};
const ViewerConditionsGroupConditions = props => {
  const {
    logicGroup,
    logicGroupIndex,
    logicGroupMatchStatusData
  } = props;
  const {
    dispatch,
    draftCondition,
    internalDraftValue,
    viewerConditionIdToHighlight,
    viewerConditionToDeleteHovered,
    isInEditMode,
    getIsUngated,
    props: {
      strategy,
      uiMessages: {
        newFilterPlaceholderMessage
      },
      useOnePanel,
      canEditLogicGroupConditions = false
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    conditionsPerORGroupLimit,
    getFilterFamilyObjectName
  } = useStrategy();
  const {
    onEditFilter
  } = useViewerActions();
  const logicGroupConditionsSeq = useMemo(() => logicGroup.conditions.entrySeq(), [logicGroup]);
  const isAddingNewCondition = draftCondition && draftCondition.conditionPath.equals(List.of(logicGroupIndex));
  const baseFilterFamily = logicGroup.filterFamily;
  const objectName = getFilterFamilyObjectName(baseFilterFamily);
  const testFiltersData = logicGroupMatchStatusData && logicGroupMatchStatusData.filtersData;
  const showQuickAddFilterButton = useMemo(() => {
    if (conditionsPerORGroupLimit === undefined) {
      return true;
    }
    invariant(conditionsPerORGroupLimit > 0, `Prop 'conditionsPerORGroupLimit' (${conditionsPerORGroupLimit}) must be bigger than 0`);
    invariant(logicGroupConditionsSeq.size <= conditionsPerORGroupLimit, `Group ${logicGroupIndex + 1} has ${logicGroupConditionsSeq.size} conditions, exceeding limit of ${conditionsPerORGroupLimit}`);
    return logicGroupConditionsSeq.size < conditionsPerORGroupLimit;
  }, [conditionsPerORGroupLimit, logicGroupConditionsSeq.size, logicGroupIndex]);
  const onConditionHighlightFinish = useCallback(() => {
    dispatch({
      type: 'viewerConditionIdToHighlight',
      payload: undefined
    });
  }, [dispatch]);
  if (logicGroupConditionsSeq.size === 0) {
    return /*#__PURE__*/_jsx(EmptyConditionsContainer, {
      useOnePanel: !!useOnePanel,
      children: newFilterPlaceholderMessage || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "newCustomerDataFilters.filtersViewer.newFilterPlaceholder"
      })
    });
  }
  const showFilterGroupLogicGroupToggle =
  // has the consuming app opted-in
  canEditLogicGroupConditions &&
  // does the strategy support it (only ILS has supported)
  strategy.canEditLogicGroupCondition && hasFlexibleLogicGroupConditions() && isInEditMode;
  const logicOperator = internalDraftValue === null || internalDraftValue === void 0 ? void 0 : internalDraftValue.getIn(['conditions', String(logicGroupIndex), 'logicGroupName']);
  return /*#__PURE__*/_jsxs(ConditionsContainer, {
    children: [showFilterGroupLogicGroupToggle && /*#__PURE__*/_jsx(View, {
      as: "div",
      marginBlockStart: "400",
      children: /*#__PURE__*/_jsx(FilterGroupLogicGroupToggle, {
        logicGroupIndex: logicGroupIndex
      })
    }), logicGroupConditionsSeq.map(([key, condition], index) => {
      const conditionPath = List([logicGroupIndex, key]);
      /*  Render child filter branches */
      if (isLogicGroup(condition)) {
        const conditionIds = condition.conditions.entrySeq().filter(([__conditionKey, conditionValue]) => !isLogicGroup(conditionValue)).map(([conditionKey]) => getConditionId(conditionPath.push(conditionKey))).concat(getConditionId(conditionPath));
        const shouldHighlight = !useOnePanel && conditionIds.includes(viewerConditionIdToHighlight);
        const matchStatusData = testFiltersData && testFiltersData[index];
        return /*#__PURE__*/_jsxs(RefHighlightContainer, {
          onHighlightFinish: onConditionHighlightFinish,
          shouldHighlight: shouldHighlight,
          targetSelector: ".condition-box",
          children: [/*#__PURE__*/_jsx(ViewerRelatedConditions, {
            conditionPath: conditionPath,
            isFirst: index === 0,
            logicGroup: condition,
            matchStatusData: matchStatusData
            // @ts-expect-error logicGroupName technically might be undefined
            ,
            parentLogicGroupOperator: logicGroup.logicGroupName
          }), matchStatusData && matchStatusData.status && /*#__PURE__*/_jsx(ViewerTestFiltersGroupMatchDisplay, {
            matchStatus: matchStatusData.status,
            objectName: objectName
          })]
        }, `${index}-${key}`);
      }

      /* Render branch filters */
      const testFilterData = testFiltersData && testFiltersData[index];
      return /*#__PURE__*/_jsxs(RefHighlightContainer, {
        onHighlightFinish: onConditionHighlightFinish,
        shouldHighlight: getConditionId(conditionPath) === viewerConditionIdToHighlight,
        targetSelector: ".condition-box",
        children: [/*#__PURE__*/_jsx(ViewerConditionClickableBox, {
          className:
          // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'List<string> | undefined' is not... Remove this comment to see the full error message
          conditionPath.equals(viewerConditionToDeleteHovered) ? 'mouse-over-delete' : '',
          deleteOnly: isNonEditableCondition(condition),
          condition: condition,
          conditionPath: conditionPath,
          filterFamily: baseFilterFamily,
          onClick: () => onEditFilter(conditionPath, baseFilterFamily),
          testFilterData: testFilterData,
          children: /*#__PURE__*/_jsx(ViewerCondition, {
            condition: condition,
            filterFamily: baseFilterFamily,
            conditionPath: conditionPath,
            isFirst: index === 0,
            parentLogicGroup: logicGroup,
            testFilterData: testFilterData
          })
        }), testFilterData && /*#__PURE__*/_jsx(ViewerTestFiltersPropertyValueDisplay, {
          objectName: objectName,
          operator: condition,
          testFilterData: testFilterData,
          getIsUngated: getIsUngated
        })]
      }, `${index}-${key}`);
    }), isAddingNewCondition && /*#__PURE__*/_jsx(ViewerConditionPlaceholder, {
      logicOperator: logicOperator,
      use: "outer"
    }), !isAddingNewCondition && showQuickAddFilterButton && /*#__PURE__*/_jsx(ViewerConditionsGroupQuickAddFilter, {
      logicGroupIndex: logicGroupIndex,
      logicOperator: logicOperator
    })]
  });
};
export default ViewerConditionsGroupConditions;